import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import useScrollReveal from '../hooks/useScrollReveal';
import { mergeFeaturedFarmers } from '../data/farmers';
import Seo from '../components/Seo';

export default function FarmersPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('fulltime');
  const [farmers, setFarmers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewportWidth, setViewportWidth] = useState(() => (
    typeof window === 'undefined' ? 1280 : window.innerWidth
  ));
  const [activeIndex, setActiveIndex] = useState(0);
  const [expandedFarmerId, setExpandedFarmerId] = useState(null);
  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const pointerStartRef = useRef(null);
  useScrollReveal();

  useEffect(() => {
    setLoading(true);
    setFarmers(mergeFeaturedFarmers([]));
    setLoading(false);
  }, []);

  useEffect(() => {
    setShowSwipeHint(true);
  }, [activeTab]);

  useEffect(() => {
    function handleResize() {
      setViewportWidth(window.innerWidth);
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const tabFarmers = useMemo(
    () => farmers.filter((farmer) => (farmer.farmerType || 'fulltime') === activeTab),
    [farmers, activeTab]
  );

  const filteredFarmers = tabFarmers;
  const farmerRingRadius = useMemo(() => {
    const count = filteredFarmers.length;
    if (count <= 1) return viewportWidth < 768 ? 146 : 270;

    const baseRadius = viewportWidth < 768 ? 146 : 270;
    const estimatedCardWidth = viewportWidth < 768
      ? Math.min(252, viewportWidth * 0.8)
      : Math.min(320, viewportWidth * 0.7);
    const requiredRadius = Math.ceil((estimatedCardWidth / (2 * Math.tan(Math.PI / count))) + 24);

    return Math.max(baseRadius, requiredRadius);
  }, [filteredFarmers.length, viewportWidth]);

  useEffect(() => {
    setActiveIndex(0);
    setExpandedFarmerId(null);
    setHasUserInteracted(false);
  }, [activeTab]);

  useEffect(() => {
    if (!filteredFarmers.length) return;
    setActiveIndex((current) => Math.min(current, filteredFarmers.length - 1));
  }, [filteredFarmers]);

  useEffect(() => {
    if (loading || !filteredFarmers.length || hasUserInteracted || expandedFarmerId) return undefined;
    const timerId = window.setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % filteredFarmers.length);
    }, 2800);
    return () => window.clearInterval(timerId);
  }, [loading, filteredFarmers.length, hasUserInteracted, expandedFarmerId]);

  const activeFarmer = filteredFarmers.length ? filteredFarmers[activeIndex] : null;
  const angleStep = filteredFarmers.length ? 360 / filteredFarmers.length : 0;

  const farmerItems = useMemo(() => {
    if (!filteredFarmers.length) return [];
    return filteredFarmers.map((farmer, index) => {
      const rawOffset = index - activeIndex;
      const half = Math.floor(filteredFarmers.length / 2);
      let offset = rawOffset;
      if (offset > half) offset -= filteredFarmers.length;
      if (offset < -half) offset += filteredFarmers.length;
      const angle = offset * angleStep;
      const radians = (angle * Math.PI) / 180;
      const depth = Math.cos(radians);
      const scale = 0.82 + (((depth + 1) / 2) * 0.18);
      const opacity = 0.26 + (((depth + 1) / 2) * 0.74);
      const lift = (1 - depth) * 18;
      return { farmer, index, angle, depth, scale, opacity, lift };
    });
  }, [filteredFarmers, activeIndex, angleStep]);

  function dismissSwipeHint() {
    if (!showSwipeHint) return;
    setShowSwipeHint(false);
  }

  function rotate(direction) {
    if (!filteredFarmers.length) return;
    setHasUserInteracted(true);
    dismissSwipeHint();
    setActiveIndex((prev) => (prev + direction + filteredFarmers.length) % filteredFarmers.length);
  }

  function handleFarmerClick(farmer, index, isActive) {
    setHasUserInteracted(true);
    dismissSwipeHint();
    if (isActive) {
      setExpandedFarmerId((prev) => (prev === farmer.id ? null : farmer.id));
      return;
    }
    setActiveIndex(index);
    setExpandedFarmerId(null);
  }

  function handlePointerStart(clientX) {
    setHasUserInteracted(true);
    dismissSwipeHint();
    pointerStartRef.current = clientX;
  }

  function handlePointerEnd(clientX) {
    if (pointerStartRef.current == null) return;
    const delta = clientX - pointerStartRef.current;
    pointerStartRef.current = null;
    if (Math.abs(delta) < 36) return;
    if (delta < 0) rotate(1);
    if (delta > 0) rotate(-1);
  }

  function getFarmerDescription(farmer) {
    const paragraphs = String(farmer.story || farmer.description || farmer.notes || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (paragraphs.length) return paragraphs[0];
    const cropsText = (farmer.crops || []).join(' / ');
    return cropsText ? `${cropsText}を中心に取り組んでいます。` : '詳細情報は準備中です。';
  }

  function getFarmerExpandedSummary(farmer) {
    const paragraphs = String(farmer.story || farmer.description || farmer.notes || '')
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (paragraphs.length >= 2) return paragraphs.slice(1, 3);
    if (paragraphs.length === 1) return [];
    const summary = [
      `${getFarmerLocation(farmer)}で活動しています。`.trim(),
      (farmer.crops || []).length ? `主な作物: ${(farmer.crops || []).join(' / ')}` : '詳細情報は準備中です。',
    ];
    return summary;
  }

  function getFarmerLocation(farmer) {
    return farmer.fieldAddress || [farmer.prefecture, farmer.city].filter(Boolean).join(' ');
  }

  function getFarmerTags(farmer) {
    const tags = [];
    if (farmer.farmerType === 'fulltime') tags.push('専業農家');
    if (farmer.farmerType === 'parttime') tags.push('ライフスタイル農家');
    (farmer.crops || []).forEach((crop) => {
      if (crop && !tags.includes(crop)) tags.push(crop);
    });
    return tags;
  }

  function getFarmerDetailPath(farmer) {
    return `/farmers/${farmer.slug || farmer.id}`;
  }

  return (
    <>
      <Seo
        title="参加農家さん"
        description="Drs.Farmに参加する専業農家さん・ライフスタイル農家さんの栽培背景や取り組みを一覧で確認できます。"
        path="/farmers"
      />
      <h1>参加農家さん</h1>
      <p className="page-lead farmer-page-lead">専業農家さんとライフスタイル農家さんを切り替えながら、参加農家さんをご覧いただけます。</p>

      <div className="tab-row" role="tablist" aria-label="農家区分">
        <button
          type="button"
          className={`tab-btn ${activeTab === 'fulltime' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('fulltime')}
        >
          専業農家
        </button>
        <button
          type="button"
          className={`tab-btn ${activeTab === 'parttime' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('parttime')}
        >
          ライフスタイル農家
        </button>
      </div>

      <section className="panel reveal farmer-disk-panel" data-reveal>
        <p className="section-subcopy">円盤を回して、参加農家をご覧ください</p>
        {loading ? <p>読み込み中...</p> : filteredFarmers.length === 0 ? <p>該当する農家が見つかりません。</p> : (
          <div className={`farmer-disk-shell ${expandedFarmerId ? 'has-expanded' : ''}`} aria-live="polite">
            <div className="farmer-disk-controls">
              <button type="button" className="btn btn-secondary farmer-nav-btn farmer-nav-btn-prev" onClick={() => rotate(-1)}>
                前へ
              </button>
              <button type="button" className="btn btn-secondary farmer-nav-btn farmer-nav-btn-next" onClick={() => rotate(1)}>
                次へ
              </button>
            </div>
            <div
              className={`farmer-disk-stage ${expandedFarmerId ? 'has-expanded' : ''}`}
              style={{ '--farmer-ring-radius': `${farmerRingRadius}px` }}
              onTouchStart={(e) => handlePointerStart(e.touches[0].clientX)}
              onTouchEnd={(e) => handlePointerEnd(e.changedTouches[0].clientX)}
              onMouseDown={(e) => handlePointerStart(e.clientX)}
              onMouseUp={(e) => handlePointerEnd(e.clientX)}
              onMouseLeave={() => {
                pointerStartRef.current = null;
              }}
            >
              <div className="farmer-disk-base" aria-hidden="true" />
              {showSwipeHint && !expandedFarmerId ? (
                <button
                  type="button"
                  className="farmer-swipe-hint"
                  onClick={dismissSwipeHint}
                  aria-label="スワイプ案内を閉じる"
                >
                  <span className="farmer-swipe-hint-text">右へスワイプして回転</span>
                  <span className="farmer-swipe-hint-arrow" aria-hidden="true">→</span>
                </button>
              ) : null}
              <div className="farmer-disk-track" role="list" aria-label="参加農家">
                {farmerItems.map(({ farmer, index, angle, depth, scale, opacity, lift }) => {
                  const isActive = index === activeIndex;
                  const isExpanded = isActive && expandedFarmerId === farmer.id;
                  const expandedSummary = getFarmerExpandedSummary(farmer);
                  const cardTransform = isExpanded
                    ? `translate3d(-50%, -50%, 0) translateY(${lift + 22}px) scale(1)`
                    : `translate3d(-50%, -50%, 0) rotateY(${angle}deg) translateZ(var(--farmer-ring-radius)) rotateY(${-angle}deg) translateY(${lift}px) scale(${scale})`;
                  return (
                    <article
                      key={farmer.id}
                      className={`farmer-disk-card ${isActive ? 'is-active' : ''} ${isExpanded ? 'is-expanded' : ''}`}
                      style={{
                        transform: cardTransform,
                        opacity: isExpanded ? 1 : expandedFarmerId ? Math.min(opacity, 0.08) : opacity,
                        zIndex: isExpanded ? 220 : Math.round((depth + 1) * 100)
                      }}
                      onClick={() => handleFarmerClick(farmer, index, isActive)}
                      role="listitem"
                    >
                      {farmer.heroImageUrl ? (
                        <div className="farmer-disk-media">
                          <img
                            className={isExpanded ? 'farmer-disk-image is-expanded' : 'farmer-disk-image'}
                            src={farmer.heroImageUrl}
                            alt={`${farmer.name}の圃場`}
                            loading="lazy"
                          />
                        </div>
                      ) : null}
                      <div className="card-body">
                        <div className="farmer-card-head">
                          <h3>{farmer.name}</h3>
                          {isExpanded ? (
                            <button
                              type="button"
                              className="farmer-inline-close"
                              onClick={(e) => {
                                e.stopPropagation();
                                setExpandedFarmerId(null);
                              }}
                            >
                              CLOSE
                            </button>
                          ) : null}
                        </div>
                        {farmer.ownerName ? <p className="meta-text">{farmer.ownerName}</p> : null}
                        {isExpanded ? (
                          <div className="farmer-expanded-meta">
                            {getFarmerLocation(farmer) ? (
                              <span className="meta-text">{getFarmerLocation(farmer)}</span>
                            ) : null}
                            {getFarmerTags(farmer).length ? (
                              <div className="tag-row" aria-label="農家タグ">
                                {getFarmerTags(farmer).map((tag) => (
                                  <span key={`${farmer.id}-${tag}`} className="pill">{tag}</span>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        <p className={`farmer-description ${isExpanded ? '' : 'is-clamped'}`}>{getFarmerDescription(farmer)}</p>
                        {isExpanded && expandedSummary.length ? (
                          <div className="farmer-inline-summary">
                            {expandedSummary.map((line, summaryIndex) => (
                              <p key={`${farmer.id}-summary-${summaryIndex}`}>{line}</p>
                            ))}
                          </div>
                        ) : null}
                        <div className="farmer-card-actions">
                          {!isExpanded ? (
                            <span className="meta-text farmer-card-hint">
                              {isActive ? 'クリックで紹介を見る' : 'クリックで中央に表示'}
                            </span>
                          ) : null}
                          {isExpanded ? (
                            <button
                              type="button"
                              className="text-btn button-reset"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(getFarmerDetailPath(farmer));
                              }}
                            >
                              生産背景を見る
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
            {activeFarmer && !expandedFarmerId ? (
              <div className="farmer-disk-quick-link">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => navigate(getFarmerDetailPath(activeFarmer))}
                >
                  {activeFarmer.name} さんの詳細へ
                </button>
              </div>
            ) : null}
          </div>
        )}
      </section>

      <section className="panel reveal" data-reveal>
        <h2>参加区分について</h2>
        <div className="audience-split">
          <article
            className={`audience-panel ${activeTab === 'fulltime' ? 'is-selected' : ''}`}
            onClick={() => setActiveTab('fulltime')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveTab('fulltime');
              }
            }}
          >
            <p className="audience-kicker">Full-time Farmers</p>
            <h3>専業農家さん</h3>
            <p>
              日々の営みの中心に農業を据え、地域の食を支える実践を積み重ねている皆さんです。
              栽培技術や出荷の工夫だけでなく、暮らしの基盤として農を続ける姿勢そのものが、
              Drs.Farmにとって大切な学びになっています。
            </p>
          </article>
          <article
            className={`audience-panel ${activeTab === 'parttime' ? 'is-selected' : ''}`}
            onClick={() => setActiveTab('parttime')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveTab('parttime');
              }
            }}
          >
            <p className="audience-kicker">Lifestyle Farmers</p>
            <h3>ライフスタイル農家さん</h3>
            <p>
              仕事や暮らしの中に農を取り入れながら、食の安心や地域とのつながりを丁寧に育てている皆さんです。
              規模の大小ではなく、それぞれの環境で誠実に取り組む姿勢が、
              持続可能な食の広がりにつながると考えています。
            </p>
          </article>
        </div>
      </section>
    </>
  );
}
